import { LEVEL_TIERS, LEVEL_ZERO_SKIN, type LevelTier } from '@/utils/levels';

export type LevelUpPhase = 'idle' | 'charge' | 'burst' | 'hero' | 'rewards';

/**
 * Fases da sequência, com o instante (ms) em que cada uma entra.
 *
 * O ritmo é intencionalmente folgado: a barra leva 3,1s pra encher (o
 * `levelsLuFill` do overlay acompanha esse número), fica meio segundo cheia
 * antes do estouro, e o elo novo tem palco só pra ele antes do botão entrar. Na
 * versão apertada o estouro comia a chegada da barra e o fim subia antes de dar
 * tempo de ler o nome do elo.
 *
 * `rewards` é o nome histórico da fase final: ela já listou prêmios, hoje só
 * traz o "Continuar" e destrava a saída pelo fundo. Por isso ela entra mais
 * cedo do que entrava — não há lista pra revelar, só o pitch pra ler.
 *
 * A encenação não é mais interrompível por clique no fundo — só pelo X ou pelo
 * Esc, que são deliberados. Um esbarrão no meio da tela cortava a festa.
 */
const PHASE_SCRIPT: { phase: LevelUpPhase; at: number }[] = [
  { phase: 'charge', at: 240 },
  { phase: 'burst', at: 3820 },
  { phase: 'hero', at: 4300 },
  { phase: 'rewards', at: 5500 },
];

const STORAGE_PREFIX = 'levels:celebrated-tier';

/**
 * Celebração de subida de elo — global, não da tela de níveis.
 *
 * Store (e não composable) porque o estado tem que ser único: o jogador sobe de
 * elo APOSTANDO, em qualquer rota, então o gatilho vive no `app.vue` e não pode
 * duplicar. Duas instâncias divergiriam e a animação dispararia duas vezes.
 */
export const useLevelUpCelebrationStore = defineStore(
  'levelUpCelebration',
  () => {
    const phase = ref<LevelUpPhase>('idle');
    const fromTier = ref<LevelTier | null>(null);
    const toTier = ref<LevelTier | null>(null);

    /** Alterna a cada execução para reiniciar os keyframes de entrada. */
    const runId = ref(0);

    /**
     * Elo que subiu mas ainda não foi celebrado, porque o jogador está no meio
     * de um jogo. Fica guardado até dar para exibir.
     */
    const deferredTierIndex = ref<number | null>(null);

    const isOpen = computed(() => phase.value !== 'idle');

    let timers: ReturnType<typeof setTimeout>[] = [];

    function clearTimers() {
      timers.forEach(clearTimeout);
      timers = [];
    }

    /* ---------------- memória do que já foi celebrado ---------------- */

    function storageKey(userId: string | number) {
      return `${STORAGE_PREFIX}:${userId}`;
    }

    /**
     * Último elo celebrado, por usuário. Sem isso um F5 relê o perfil do zero e
     * a animação dispararia de novo a cada carga — ou, se guardássemos só em
     * memória, nunca dispararia depois de uma navegação.
     *
     * A chave leva o id do usuário para não vazar entre contas no mesmo browser.
     */
    function readCelebratedTier(userId: string | number): number | null {
      if (!import.meta.client) return null;

      const raw = localStorage.getItem(storageKey(userId));

      if (raw === null) return null;

      const parsed = Number(raw);

      return Number.isFinite(parsed) ? parsed : null;
    }

    function writeCelebratedTier(userId: string | number, tierIndex: number) {
      if (!import.meta.client) return;

      try {
        localStorage.setItem(storageKey(userId), String(tierIndex));
      } catch {
        // Modo privativo / cota cheia: perder a marca só faz a animação repetir,
        // não é motivo pra estourar a tela.
      }
    }

    /* ---------------- execução ---------------- */

    function play(targetIndex: number) {
      const target = LEVEL_TIERS[targetIndex];

      if (!target) return;

      clearTimers();

      // A festa do Bronze (target 0) vem do degrau "Sem elo", não de outro
      // elo — caminhos locais como os de LEVEL_TIERS (o CDN não é resolvível
      // aqui fora do setup do Nuxt).
      fromTier.value =
        targetIndex === 0
          ? {
              ...LEVEL_TIERS[0],
              key: 'lvl0',
              name: 'Sem elo',
              color: LEVEL_ZERO_SKIN.color,
              accent: LEVEL_ZERO_SKIN.accent,
              badge: LEVEL_ZERO_SKIN.badge,
              pillar: LEVEL_ZERO_SKIN.pillar,
              isUnranked: true,
            }
          : LEVEL_TIERS[Math.max(0, targetIndex - 1)];
      toTier.value = target;
      phase.value = 'idle';
      runId.value += 1;

      PHASE_SCRIPT.forEach(({ phase: next, at }) => {
        timers.push(
          setTimeout(() => {
            phase.value = next;
          }, at),
        );
      });
    }

    function close() {
      clearTimers();
      phase.value = 'idle';
    }

    /** Guarda o elo pra exibir quando o jogador sair do jogo. */
    function defer(tierIndex: number) {
      deferredTierIndex.value = tierIndex;
    }

    function playDeferred() {
      const pending = deferredTierIndex.value;

      if (pending === null) return;

      deferredTierIndex.value = null;
      play(pending);
    }

    function $reset() {
      clearTimers();
      phase.value = 'idle';
      fromTier.value = null;
      toTier.value = null;
      deferredTierIndex.value = null;
    }

    return {
      phase,
      fromTier,
      toTier,
      runId,
      isOpen,
      deferredTierIndex,
      play,
      close,
      defer,
      playDeferred,
      readCelebratedTier,
      writeCelebratedTier,
      $reset,
    };
  },
);
